import React, { useState, useEffect } from 'react';
import { Menu as MenuIcon, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', path: '/' },
    {
      label: 'About Us',
      path: '/about',
      dropdown: [
        { label: 'District 9', path: '/district-9' },
        { label: 'Staff Directory', path: '/staff' },
        { label: 'Community Connections', path: '/partnerships' }
      ]
    },
    {
      label: 'Parents',
      path: '#',
      dropdown: [
        { label: 'Parent Advisory Committee', path: '/parent-advisory' },
        { label: 'Stay Informed', path: '/stay-informed' }
      ]
    },
    { label: 'Students', path: '/students' },
    { label: 'School Calendar', path: '/calendar' },
    { label: 'Contact Us', path: '/contact' },
    { label: 'Enrollment', path: '/enrollment' }
  ];

  return (
    <nav
      aria-label="Main navigation"
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${scrolled ? 'py-2' : 'py-4'}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main navbar container - enhanced glass effect */}
        <div className={`px-6 py-3 shadow-lg border-2 rounded-3xl lg:rounded-full transition-all duration-300 bg-[#123f1e] ${
          scrolled
            ? 'border-[#febc04]'
            : 'border-[#febc04]/30'
        }`}>
          <div className="flex justify-between items-center">
            {/* Logo and Brand */}
            <Link to="/" className="flex items-center gap-3 group focus:outline-none focus:ring-2 focus:ring-[#febc04] focus:ring-offset-2 focus:ring-offset-[#123f1e] rounded-full">
              <div className="relative">
                <div className="absolute inset-0 bg-[#febc04] rounded-full blur-md opacity-40 group-hover:opacity-60 transition-opacity duration-200"></div>
                <img
                  className="relative h-10 w-10 object-contain rounded-full bg-white p-1 shadow-sm"
                  src="/assets/images/home/gardenoflearning-logo.png"
                  alt="Garden of Learning & Discovery Logo"
                />
              </div>
              <div className="hidden sm:block">
                <span className="text-base font-black text-white leading-tight tracking-wide">
                  Garden of Learning
                </span>
                <p className="text-xs text-[#febc04] font-bold uppercase tracking-wider">
                  District 9 Pre-K
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navItems.map((item, index) => (
                <div
                  key={index}
                  className="relative"
                  onMouseEnter={() => item.dropdown && setActiveDropdown(index)}
                  onMouseLeave={() => {
                    if (item.dropdown) {
                      setTimeout(() => setActiveDropdown(null), 200);
                    }
                  }}
                >
                  {item.dropdown ? (
                    <>
                      {item.path === '#' ? (
                        <button
                          type="button"
                          aria-expanded={activeDropdown === index}
                          aria-haspopup="true"
                          className="flex items-center gap-1 px-4 py-2.5 text-sm font-bold text-white/90 hover:text-[#febc04] transition-colors duration-200 rounded-full hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-[#febc04]/50"
                        >
                          {item.label}
                          <ChevronDown className="w-4 h-4 stroke-[3]" />
                        </button>
                      ) : (
                        <Link
                          to={item.path}
                          aria-expanded={activeDropdown === index}
                          aria-haspopup="true"
                          aria-current={location.pathname === item.path ? 'page' : undefined}
                          className="flex items-center gap-1 px-4 py-2.5 text-sm font-bold text-white/90 hover:text-[#febc04] transition-colors duration-200 rounded-full hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-[#febc04]/50"
                        >
                          {item.label}
                          <ChevronDown className="w-4 h-4 stroke-[3]" />
                        </Link>
                      )}

                      {activeDropdown === index && (
                        <div
                          className="absolute top-full pt-4 left-0 min-w-[240px]"
                          onMouseEnter={() => setActiveDropdown(index)}
                          onMouseLeave={() => setActiveDropdown(null)}
                        >
                          <div className="bg-white rounded-3xl shadow-xl border-2 border-[#febc04] overflow-hidden p-2">
                            {item.dropdown.map((subItem, subIndex) => (
                              <Link
                                key={subIndex}
                                to={subItem.path}
                                aria-current={location.pathname === subItem.path ? 'page' : undefined}
                                className="block px-4 py-3 text-sm font-bold text-[#123f1e] hover:bg-[#febc04] hover:text-[#123f1e] rounded-xl transition-all duration-200"
                              >
                                {subItem.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      aria-current={location.pathname === item.path ? 'page' : undefined}
                      className="px-4 py-2.5 text-sm font-bold text-white/90 hover:text-[#febc04] transition-colors duration-200 rounded-full hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-[#febc04]/50"
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2.5 text-white hover:bg-white/10 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#febc04]"
                aria-label="Toggle menu"
                aria-expanded={isOpen}
                aria-controls="mobile-menu"
              >
                {isOpen ? <X size={24} className="stroke-[3]" /> : <MenuIcon size={24} className="stroke-[3]" />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {/* `invisible` when closed: max-h-0 + opacity-0 alone left every link in the tab order and
              the accessibility tree while nothing was on screen (WCAG 2.4.3 / 2.4.7). visibility
              transitions discretely, so the open/close animation is unchanged. */}
          <div id="mobile-menu" className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0 invisible'
          }`}>
            <div className="mt-4 pt-4 pb-4 border-t border-white/20 -mx-6 px-6">
              <div className="flex flex-col gap-2">
                {navItems.map((item, index) => (
                  <div key={index}>
                    {item.dropdown ? (
                      <div>
                        <div className="text-[#febc04] px-4 py-2 text-sm font-black uppercase tracking-wider">
                          {item.label}
                        </div>
                        <div className="pl-4 space-y-1">
                          {item.dropdown.map((subItem, subIndex) => (
                            <Link
                              key={subIndex}
                              to={subItem.path}
                              aria-current={location.pathname === subItem.path ? 'page' : undefined}
                              className="block text-white/90 hover:text-[#febc04] px-4 py-3 text-sm font-bold transition-colors duration-200 rounded-xl hover:bg-white/5"
                              onClick={() => setIsOpen(false)}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={item.path}
                        aria-current={location.pathname === item.path ? 'page' : undefined}
                        className="block text-white/90 hover:text-[#febc04] px-4 py-3 text-sm font-black transition-colors duration-200 rounded-xl hover:bg-white/5"
                        onClick={() => setIsOpen(false)}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
